import { formatInr } from "@/utils/basicFunctions.util";
import { useState } from "react";
import { fillTemplate } from "../../../guidePage.util";
import type { ProgrammePlannerCopy } from "../programmePlanner.component";

// Everything the planner shows is derived from five inputs: the business,
// the goal, the typical bill, the margin and the reward rate.
export function useProgrammePlanner(copy: ProgrammePlannerCopy) {
  const initialState = { ...copy.initial };
  const [state, setState] = useState(initialState);

  const update = (patch: Partial<typeof initialState>) =>
    setState((state) => ({ ...state, ...patch }));

  const business =
    copy.businesses.find((item) => item.key === state.business) ??
    copy.businesses[0];
  const goal = copy.goals.find((item) => item.key === state.goal) ?? copy.goals[0];

  const cost = (state.bill * state.rate) / 100;
  const grossMargin = (state.bill * state.margin) / 100;
  const share = grossMargin > 0 ? (cost / grossMargin) * 100 : 0;
  const verdict =
    share <= 15
      ? copy.verdicts.low
      : share <= 30
        ? copy.verdicts.mid
        : copy.verdicts.high;

  return {
    state,
    setBusiness: (business: string) => update({ business }),
    setGoal: (goal: string) => update({ goal }),
    setBill: (value: string) => update({ bill: Math.max(0, Number(value) || 0) }),
    setMargin: (margin: number) => update({ margin }),
    setRate: (rate: number) => update({ rate }),
    goal,
    example: fillTemplate(goal.example, {
      item: business.item,
      goal: business.goal,
    }),
    costText: formatInr(cost),
    marginText: formatInr(grossMargin),
    shareText: `${share.toFixed(1)}%`,
    barValue: Math.min(100, share),
    verdict,
  };
}
